import * as React from 'react'
import { cn } from '../../lib/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', label, error, hint, leftIcon, rightIcon, id, ...props }, ref) => {
    const inputId = id || React.useId()

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-heading font-medium text-midnight-700 mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-400">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            id={inputId}
            className={cn(
              'w-full px-4 py-3 border rounded-xl bg-white font-body text-midnight-900',
              'placeholder:text-warm-400',
              'focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none',
              'transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-warm-50',
              error ? 'border-red-500 focus:ring-red-400' : 'border-warm-300',
              leftIcon && 'pl-12',
              rightIcon && 'pr-12',
              className
            )}
            ref={ref}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-warm-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
        {hint && !error && <p className="mt-1.5 text-sm text-warm-500">{hint}</p>}
      </div>
    )
  }
)

Input.displayName = 'Input'

// Textarea component
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id || React.useId()

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-heading font-medium text-midnight-700 mb-2"
          >
            {label}
          </label>
        )}
        <textarea
          id={inputId}
          className={cn(
            'w-full px-4 py-3 border rounded-xl bg-white font-body text-midnight-900',
            'placeholder:text-warm-400',
            'focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none',
            'transition-all duration-200 resize-none',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-warm-50',
            error ? 'border-red-500 focus:ring-red-400' : 'border-warm-300',
            className
          )}
          ref={ref}
          {...props}
        />
        {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
        {hint && !error && <p className="mt-1.5 text-sm text-warm-500">{hint}</p>}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'

// Select component
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  hint?: string
  options: Array<{ value: string; label: string }>
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, hint, options, id, ...props }, ref) => {
    const inputId = id || React.useId()

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-heading font-medium text-midnight-700 mb-2"
          >
            {label}
          </label>
        )}
        <select
          id={inputId}
          className={cn(
            'w-full px-4 py-3 border rounded-xl bg-white font-body text-midnight-900',
            'focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none',
            'transition-all duration-200 cursor-pointer',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-warm-50',
            error ? 'border-red-500 focus:ring-red-400' : 'border-warm-300',
            className
          )}
          ref={ref}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
        {hint && !error && <p className="mt-1.5 text-sm text-warm-500">{hint}</p>}
      </div>
    )
  }
)

Select.displayName = 'Select'

export { Input, Textarea, Select }
